"use client";

import { useEffect, useMemo, useState } from "react";
import type { SlipLadder } from "@/components/optimizer/props-slip-breakevens";
import type { SlipPrice } from "@/lib/fantasy-props/slip";

export type QuoteState<Q> =
  | { readonly status: "pricing" }
  | { readonly status: "priced"; readonly quote: Q; readonly stale: boolean }
  | { readonly status: "unreachable" };

export type LivePricing =
  | { readonly status: "pricing" }
  | { readonly status: "unavailable"; readonly reason: "unreachable" | "mixed" | "unreadable" | "unoffered" }
  | { readonly status: "refused"; readonly detail: string }
  | { readonly status: "priced"; readonly factor: number; readonly uncutPayout: number; readonly stale: boolean };

export type SlipPricing = {
  readonly live: LivePricing | null;
  readonly price: SlipPrice | null;
  readonly tiers: SlipLadder | undefined;
  readonly mismatch: boolean;
};

const SETTLE_MS = 250;
const REFRESH_MS = 15_000;

const PRICING = { status: "pricing" } as const;
const UNREACHABLE = { status: "unreachable" } as const;

type Held<Q> = { readonly key: string; readonly quote: Q | null; readonly failed: boolean };

export function useSlipQuote<P extends { readonly key: string }, Q>(
  picks: P | null,
  fetchQuote: (picks: P, signal: AbortSignal) => Promise<Q>,
): QuoteState<Q> {
  const key = picks?.key ?? null;
  const [held, setHeld] = useState<Held<Q> | null>(null);

  useEffect(() => {
    if (picks === null) return;
    let controller: AbortController | null = null;
    const run = () => {
      controller?.abort();
      const current = new AbortController();
      controller = current;
      fetchQuote(picks, current.signal)
        .then((quote) => {
          if (!current.signal.aborted) setHeld({ key: picks.key, quote, failed: false });
        })
        .catch(() => {
          if (!current.signal.aborted) setHeld((prev) => ({ key: picks.key, quote: prev?.key === picks.key ? prev.quote : null, failed: true }));
        });
    };
    const settle = window.setTimeout(run, SETTLE_MS);
    const refresh = window.setInterval(() => {
      if (document.visibilityState === "visible") run();
    }, REFRESH_MS);
    return () => {
      window.clearTimeout(settle);
      window.clearInterval(refresh);
      controller?.abort();
    };
  }, [key]);

  return useMemo<QuoteState<Q>>(() => {
    if (held === null || held.key !== key || (held.quote === null && !held.failed)) return PRICING;
    if (held.quote === null) return UNREACHABLE;
    return { status: "priced", quote: held.quote, stale: held.failed };
  }, [held, key]);
}
